import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { ActivityIndicator, Alert, Text, View, StyleSheet, TouchableOpacity } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import * as z from "zod";
import { Ionicons } from "@expo/vector-icons";
import { useSupabase } from "@/hooks/useSupabase";
import { getBiometricButtonText, getBiometricInlinePhrase } from "@/lib/biometric";
import { Button, Form, FormField, FormInput } from "@/components/ui";
import { KeyboardAwareScrollView, KeyboardToolbar } from "react-native-keyboard-controller";
import colors from "@/constants/Colors";

// Module-scoped flag so the biometric prompt only auto-fires on the very
// first /sign-in mount per JS session. This avoids the prompt re-appearing
// after an explicit sign-out + manual navigation back to /sign-in.
let hasAutoTriggeredBiometric = false;

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address."),
  password: z
    .string()
    .min(8, "Please enter at least 8 characters.")
    .max(64, "Please enter fewer than 64 characters.")
});

export default function SignIn() {
  const {
    signInWithPassword,
    signInWithBiometric,
    enableBiometric,
    biometricAvailable,
    biometricEnabled,
    biometricLabel,
  } = useSupabase();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const biometricButtonLabel = getBiometricButtonText(biometricLabel);
  const biometricInlinePhrase = getBiometricInlinePhrase(biometricLabel);
  const biometricIconName: React.ComponentProps<typeof Ionicons>['name'] =
    biometricLabel === 'Face ID' || biometricLabel === 'face unlock'
      ? 'scan-outline'
      : 'finger-print-outline';

  const [biometricSubmitting, setBiometricSubmitting] = useState(false);
  const handleBiometricRef = useRef<(() => Promise<void>) | null>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleBiometricSignIn = async () => {
    if (biometricSubmitting) return;
    setBiometricSubmitting(true);
    try {
      // OneSignal external_id is set by AuthProvider whenever `user` changes,
      // so no per-screen OneSignal.login() call is needed here.
      await signInWithBiometric();
    } catch (error: any) {
      // User cancelled or no creds stored — silently fall back to manual.
      if (error?.biometricCancelled || error?.biometricMissing) {
        return;
      }
      // Stored creds were rejected by Supabase: tell the user.
      if (error?.biometricStale) {
        Alert.alert(
          'Saved sign-in expired',
          'Your saved credentials are no longer valid. Please sign in with your email and password.'
        );
        return;
      }
      Alert.alert(error?.message ?? 'Biometric sign-in failed.');
    } finally {
      setBiometricSubmitting(false);
    }
  };

  // Keep a stable ref so the auto-trigger effect doesn't depend on the closure.
  handleBiometricRef.current = handleBiometricSignIn;

  // Auto-trigger biometric sign-in once per JS session when conditions are met.
  useEffect(() => {
    if (hasAutoTriggeredBiometric) return;
    if (!biometricEnabled || !biometricAvailable) return;
    hasAutoTriggeredBiometric = true;
    void handleBiometricRef.current?.();
  }, [biometricEnabled, biometricAvailable]);

  async function onSubmit(data: z.infer<typeof formSchema>) {
    try {
      await signInWithPassword(data.email, data.password);
      // OneSignal external_id is set by AuthProvider whenever `user` changes,
      // so no per-screen OneSignal.login() call is needed here.
      //
      // NOTE: don't call OneSignal.Notifications.requestPermission() here.
      // It launches a native overlay activity that can race with the
      // navigation transition out of the auth stack and trigger a
      // YogaLayoutableShadowNode crash. The in-app banner that appears
      // post sign-in (when permission/subscription are missing) routes
      // the user to /enable-notifications where requestPermission is
      // called safely from within a stable, mounted screen.

      form.reset();

      // Offer to enable biometric sign-in if the device supports it.
      if (biometricAvailable && !biometricEnabled) {
        Alert.alert(
          'Sign in faster next time?',
          `Skip typing your password by signing in with ${biometricInlinePhrase}. Your credentials will be stored securely on this device.`,
          [
            { text: 'Not now', style: 'cancel' },
            {
              text: 'Set up',
              onPress: async () => {
                try {
                  await enableBiometric(data.email, data.password);
                } catch (err: any) {
                  Alert.alert(
                    'Could not set up quick sign-in',
                    err?.message ?? 'Please try again from Settings.'
                  );
                }
              },
            },
          ]
        );
      }
    } catch (error: Error | any) {
      Alert.alert(error.message)
    }
  }

  function promptResetPassword() {
    Alert.alert(
      'Password reset sent!',
      'Please check your emails for a password reset link.',
      [
        { text: 'Ok', style: 'cancel' },

      ],
      { cancelable: false }
    );
  }

  const showBiometricButton = biometricAvailable && biometricEnabled;

  return (
    <>
      <KeyboardAwareScrollView
        bottomOffset={62}
        style={styles.container}
        contentContainerStyle={{
          flexGrow: 1,
          paddingTop: 40 + insets.top,
          paddingBottom: 20 + insets.bottom,
          paddingHorizontal: 20,
        }}
      >
        <View style={styles.header}>
          <Text style={styles.title}>Welcome back</Text>
          <Text style={styles.subtitle}>Sign in to continue to Crewsaders</Text>
        </View>

        {showBiometricButton ? (
          <View style={styles.biometricContainer}>
            <TouchableOpacity
              style={styles.biometricButton}
              onPress={handleBiometricSignIn}
              disabled={biometricSubmitting}
              accessibilityRole="button"
              accessibilityLabel={biometricButtonLabel}
            >
              {biometricSubmitting ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <>
                  <Ionicons name={biometricIconName} size={22} color={colors.primary} />
                  <Text style={styles.biometricButtonText}>{biometricButtonLabel}</Text>
                </>
              )}
            </TouchableOpacity>
            <View style={styles.dividerContainer}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>or</Text>
              <View style={styles.dividerLine} />
            </View>
          </View>
        ) : null}

        <View style={styles.formContainer}>
          <Form {...form}>
            <View style={styles.inputsContainer}>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormInput
                    label="Email"
                    placeholder="Enter your email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect={false}
                    keyboardType="email-address"
                    textContentType="username"
                    className="border-gray-300 bg-white"
                    {...field}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormInput
                    label="Password"
                    placeholder="Enter your password"
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="current-password"
                    textContentType="password"
                    secureTextEntry
                    className="border-gray-300 bg-white"
                    {...field}
                  />
                )}
              />
            </View>
          </Form>
        </View>

        <View style={styles.actionsContainer}>
          <Button
            size="default"
            variant="crew"
            style={styles.signInButton}
            onPress={form.handleSubmit(onSubmit)}
          >
            {form.formState.isSubmitting ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              "Sign in"
            )}
          </Button>

          <TouchableOpacity
            style={styles.forgotPassword}
            onPress={() => {
              router.replace("/forgotten-password");
            }}
          >
            <Text style={styles.forgotPasswordText}>Forgotten your password?</Text>
          </TouchableOpacity>

          <View style={styles.signUpContainer}>
            <Text style={styles.signUpText}>
              Don't have an account?{" "}
            </Text>
            <TouchableOpacity
              onPress={() => {
                router.replace("/sign-up");
              }}
            >
              <Text style={styles.signUpLink}>Sign up</Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAwareScrollView>

      <KeyboardToolbar />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f7',
  },
  header: {
    marginBottom: 30,
    alignItems: 'center',
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#000',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  biometricContainer: {
    width: '100%',
    marginBottom: 12,
  },
  biometricButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    height: 54,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    backgroundColor: '#fff',
  },
  biometricButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.primary,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 16,
  },
  dividerLine: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
    backgroundColor: '#d0d0d0',
  },
  dividerText: {
    marginHorizontal: 12,
    color: '#888',
    fontSize: 13,
  },
  formContainer: {
    backgroundColor: 'transparent',
    borderRadius: 16,
    width: '100%',
    marginBottom: 20,
  },
  inputsContainer: {
    gap: 16,
  },
  actionsContainer: {
    width: '100%',
    marginTop: 'auto',
  },
  signInButton: {
    marginVertical: 16,
    borderRadius: 12,
    height: 54,
  },
  forgotPassword: {
    alignItems: 'center',
    marginVertical: 12,
  },
  forgotPasswordText: {
    color: '#666',
    fontSize: 15,
  },
  signUpContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 12,
  },
  signUpText: {
    color: '#666',
    fontSize: 15,
  },
  signUpLink: {
    color: colors.primary,
    fontSize: 15,
    fontWeight: 'bold',
  },
});
